import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HideImageOutlinedIcon from "@mui/icons-material/HideImageOutlined";
import ImageIcon from "@mui/icons-material/Image";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import { getAssetHierarchyPalette, getDialogSectionBoxSx, getOutlinedButtonSx } from "../assetHierarchyStyles";
import { AssetDocumentType, isOverviewImageContentType, OVERVIEW_IMAGE_CONTENT_TYPES } from "../assetDocumentEnums";
import cmmsMessages from "../cmmsMessages";
import messages from "../messages";
import useObjectUrl from "../useObjectUrl";
import { fetchAssetDocumentBlob, fetchAssetDocuments } from "../../../fetchers/cmmsAssetDocument";
import { clearAssetNodeOverviewImage, setAssetNodeOverviewImage } from "../../../fetchers/cmmsAssetHierarchy";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../cmmsRoles";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import AssetDocumentUploadDialog from "./AssetDocumentUploadDialog";
import { CmmsErrorState, CmmsListSkeleton, formatCmmsError } from "./CmmsStates";

function OverviewImagePickerDialog({ assetNodeId, cmmsColors, companyKey, currentDocumentId, isSaving, onChoose, onClose, open }) {
  const intl = useIntl();

  // The document list's own key, so after a visit to the Documents tab this is a cache hit.
  const documentsQuery = useQuery(
    ["cmmsAssetDocuments", companyKey, assetNodeId],
    () => fetchAssetDocuments(companyKey, assetNodeId),
    { enabled: Boolean(open && companyKey && assetNodeId), staleTime: 30000 },
  );

  // Only what the API will take. A HEIC or TIFF photo is still a document, just not one the card can show.
  const images = (documentsQuery.data ?? [])
    .filter((doc) => isOverviewImageContentType(doc.contentType))
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

  return (
    <Popup openPopup={open} setOpenPopup={onClose} maxWidth="sm" title={intl.formatMessage(messages.overviewImagePickerTitle)}>
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box sx={getDialogSectionBoxSx(cmmsColors)}>
          <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14 }}>
            <FormattedMessage {...messages.overviewImagePickerIntro} />
          </Typography>
        </Box>

        {documentsQuery.isLoading &&
          <CmmsListSkeleton rows={3} height={28} />
        }

        {documentsQuery.error &&
          <CmmsErrorState
            error={documentsQuery.error}
            fallback={intl.formatMessage(messages.documentsOperationFailed)}
            onRetry={() => documentsQuery.refetch()}
            isRetrying={documentsQuery.isFetching}
            dense
          />
        }

        {!documentsQuery.isLoading && !documentsQuery.error && images.length === 0 &&
          <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontStyle: "italic" }}>
            <FormattedMessage {...messages.overviewImagePickerEmpty} />
          </Typography>
        }

        {images.length > 0 &&
          <List dense disablePadding>
            {images.map((doc) => {
              const isCurrent = doc.id === currentDocumentId;
              return (
                <ListItemButton key={doc.id} disabled={isSaving || isCurrent} onClick={() => onChoose(doc)}>
                  <ListItemIcon sx={{ minWidth: 36, color: cmmsColors.mutedText }}>
                    <ImageIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.title}
                    secondary={doc.fileName}
                    primaryTypographyProps={{ sx: { color: cmmsColors.text, fontWeight: 700 } }}
                    secondaryTypographyProps={{ sx: { color: cmmsColors.mutedText, wordBreak: "break-all" } }}
                  />
                  {isCurrent &&
                    <Chip size="small" label={intl.formatMessage(messages.overviewImageChip)} sx={{ ml: 1 }} />
                  }
                </ListItemButton>
              );
            })}
          </List>
        }

        <Stack direction="row" justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose} disabled={isSaving} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
        </Stack>
      </Box>
    </Popup>
  );
}

/*
 * The overview image on the machine card.
 *
 * The image is one of the node's own documents, so nothing here stores a file of its own.
 * Upload is the ordinary document upload, preset to a photo. Choose existing picks from the
 * node's documents. Remove clears only the pointer, and the document stays in the list.
 *
 * Setting and clearing are Cmms_Manager on the API, so the buttons are too. Everyone who can
 * see the card sees the image.
 */
export default function AssetOverviewImage({ assetName, assetNodeId, companyKey, overviewImageDocumentId }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const queryClient = useQueryClient();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const documentId = overviewImageDocumentId ?? null;
  const failedMessage = intl.formatMessage(messages.overviewImageFailed);

  // A document's file never changes once uploaded (editing one touches only its title, type
  // and description), so the bytes behind an id do not go stale.
  const imageQuery = useQuery(
    ["cmmsAssetOverviewImage", companyKey, assetNodeId, documentId],
    () => fetchAssetDocumentBlob(companyKey, assetNodeId, documentId),
    { enabled: Boolean(companyKey && assetNodeId && documentId), staleTime: Infinity },
  );
  const objectUrl = useObjectUrl(documentId ? imageQuery.data : null);

  const setMutation = useMutation({ mutationFn: (id) => setAssetNodeOverviewImage(companyKey, assetNodeId, id) });
  const clearMutation = useMutation({ mutationFn: () => clearAssetNodeOverviewImage(companyKey, assetNodeId) });
  const isMutating = setMutation.isLoading || clearMutation.isLoading;

  // The card reads the id off the tree, so the tree is what has to change. The prefix covers
  // the tree both with and without inactive nodes.
  const refreshTree = () => queryClient.invalidateQueries(["cmmsAssetHierarchyTree"]);

  const handleChoose = async (doc) => {
    try {
      await setMutation.mutateAsync(doc.id);
      setIsPickerOpen(false);
      await refreshTree();
      pushNotification(intl.formatMessage(messages.overviewImageSet));
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, failedMessage));
    }
  };

  // The dialog has already said the upload worked, so success needs no second toast. If the
  // pointer fails, the photo is still in the document list and can be chosen from there.
  const handleUploaded = async (doc) => {
    try {
      await setMutation.mutateAsync(doc.id);
      await refreshTree();
    } catch (error) {
      pushErrorMessage(intl.formatMessage(messages.overviewImageUploadedNotSet, { error: formatCmmsError(error, failedMessage) }));
    }
  };

  const handleRemove = async () => {
    try {
      await clearMutation.mutateAsync();
      await refreshTree();
      pushNotification(intl.formatMessage(messages.overviewImageRemoved));
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, failedMessage));
    }
  };

  return (
    <Box>
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: cmmsColors.surface,
          border: `1px solid ${cmmsColors.border}`,
          borderRadius: 0.5,
          overflow: "hidden",
        }}
      >
        {documentId == null &&
          <Stack alignItems="center" spacing={0.5} sx={{ color: cmmsColors.mutedText }}>
            <ImageOutlinedIcon sx={{ fontSize: 40 }} />
            <Typography variant="body2" sx={{ color: cmmsColors.mutedText }}>
              <FormattedMessage {...messages.overviewImageEmpty} />
            </Typography>
          </Stack>
        }
        {documentId != null && imageQuery.isLoading &&
          <CircularProgress size={28} />
        }
        {documentId != null && imageQuery.error &&
          <CmmsErrorState
            error={imageQuery.error}
            fallback={intl.formatMessage(messages.overviewImageLoadFailed)}
            onRetry={() => imageQuery.refetch()}
            isRetrying={imageQuery.isFetching}
            dense
          />
        }
        {documentId != null && objectUrl &&
          <Box
            component="img"
            src={objectUrl}
            alt={intl.formatMessage(messages.overviewImageAlt, { name: assetName })}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        }
      </Box>

      <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setIsUploadOpen(true)}
            disabled={isMutating}
            sx={getOutlinedButtonSx(cmmsColors)}
          >
            <FormattedMessage {...messages.overviewImageUpload} />
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PhotoLibraryIcon />}
            onClick={() => setIsPickerOpen(true)}
            disabled={isMutating}
            sx={getOutlinedButtonSx(cmmsColors)}
          >
            <FormattedMessage {...messages.overviewImageChoose} />
          </Button>
          {documentId != null &&
            <Button
              size="small"
              variant="outlined"
              startIcon={<HideImageOutlinedIcon />}
              onClick={handleRemove}
              disabled={isMutating}
              sx={getOutlinedButtonSx(cmmsColors)}
            >
              <FormattedMessage {...messages.overviewImageRemove} />
            </Button>
          }
        </Stack>
      </HideIfNoRole>

      <AssetDocumentUploadDialog
        assetNodeId={assetNodeId}
        companyKey={companyKey}
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        initialDocumentType={AssetDocumentType.Photo}
        acceptedContentTypes={OVERVIEW_IMAGE_CONTENT_TYPES}
        onUploaded={handleUploaded}
      />

      <OverviewImagePickerDialog
        assetNodeId={assetNodeId}
        cmmsColors={cmmsColors}
        companyKey={companyKey}
        currentDocumentId={documentId}
        isSaving={isMutating}
        onChoose={handleChoose}
        onClose={() => setIsPickerOpen(false)}
        open={isPickerOpen}
      />
    </Box>
  );
}
